import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

// Add token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Campaign APIs
export const getCampaigns = (params) => api.get('/campaigns', { params });
export const getCampaign = (id) => api.get(`/campaigns/${id}`);
export const createCampaign = (data) => api.post('/campaigns', data);
export const updateCampaign = (id, data) => api.put(`/campaigns/${id}`, data);
export const deleteCampaign = (id) => api.delete(`/campaigns/${id}`);

// Asset APIs
export const getCampaignAssets = (campaignId) => api.get(`/campaigns/${campaignId}/assets`);
export const getAsset = (assetId) => api.get(`/assets/${assetId}`).then(res => res.data);
export const createAsset = (data) => api.post('/assets', data);
export const updateAsset = (id, data) => api.put(`/assets/${id}`, data);
export const deleteAsset = (id) => api.delete(`/assets/${id}`);
export const personalizeAsset = (assetId, personalizeData) => api.post(`/assets/${assetId}/personalize`, personalizeData);
export const generateWithAIPrompt = (assetId, generateData) => api.post(`/assets/${assetId}/generate-with-ai`, generateData);
// Company APIs
export const getCompany = () => api.get('/company');
export const updateCompany = (data) => api.put('/company', data);

// Customer & Property APIs
export const getCustomers = () => api.get('/customers');
export const createCustomer = (data) => api.post('/customers', data);
export const getProperties = () => api.get('/properties');
export const createProperty = (data) => api.post('/properties', data);
export const getServiceHistory = (propertyId) => 
  api.get('/service-history', { params: { property_id: propertyId } });
export const createServiceHistory = (data) => api.post('/service-history', data);

// KPI APIs
export const getKPIs = (params) => api.get('/kpis', { params });
export const createKPI = (data) => api.post('/kpis', data);

// Checklist APIs
export const getChecklists = (campaignId) => 
  api.get('/checklists', { params: { campaign_id: campaignId } });
export const createChecklist = (data) => api.post('/checklists', data);
export const updateChecklist = (id, data) => api.put(`/checklists/${id}`, data);

// Dashboard APIs
export const getDashboardStats = () => api.get('/dashboard/stats');

// File Upload APIs
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getFile = (fileId) => api.get(`/files/${fileId}`, { responseType: 'blob' });

// Public APIs
export const getPublicCampaign = (campaignId, ownerParam) =>
  axios.get(`${API_URL}/api/public/campaign/${campaignId}`, { params: ownerParam ? { u: ownerParam } : {} });

export const submitPublicLead = (campaignId, payload) =>
  axios.post(`${API_URL}/api/public/campaign/${campaignId}/lead`, payload);

// AI Generation APIs
export const generateAssetsWithAI = (campaignId, assetTypes) =>
  api.post('/ai/generate-assets', { campaign_id: campaignId, asset_types: assetTypes });

// Training Vault APIs
export const getTrainingChannels = () => api.get('/training/channels').then(res => res.data);
export const getTrainingChannel = (id) => api.get(`/training/channel/${id}`).then(res => res.data);
export const getTrainingLesson = (id) => api.get(`/training/lesson/${id}`).then(res => res.data);
export const updateTrainingProgress = (lessonId, data) => api.post(`/training/lesson/${lessonId}/progress`, data);
export const getTrainingProgress = () => api.get('/training/progress').then(res => res.data);

// Pricing & Checkout APIs
export const getPricingConfig = () => api.get('/pricing/config').then(res => res.data);
export const getUserEntitlements = () => api.get('/user/entitlements').then(res => res.data);
export const createCheckoutSession = (data) => api.post('/checkout/create-session', data);

// Marketing Automation APIs
export const getAutoClients = () => api.get('/automation/clients');
export const createAutoClient = (data) => api.post('/automation/clients', data);
export const updateAutoClient = (id, data) => api.put(`/automation/clients/${id}`, data);
export const deleteAutoClient = (id) => api.delete(`/automation/clients/${id}`);
export const generateAutoContent = (data) => api.post('/automation/generate', data);
export const getAutoContent = (params) => api.get('/automation/content', { params });
export const editAutoContent = (id, data) => api.put(`/automation/content/${id}`, data);
export const approveAutoContent = (id, data) => api.post(`/automation/content/${id}/approve`, data);
export const rejectAutoContent = (id, data) => api.post(`/automation/content/${id}/reject`, data);
export const publishAutoContent = (id) => api.post(`/automation/content/${id}/publish`);
export const getAutoCalendar = (params) => api.get('/automation/calendar', { params });
export const getAutoDashboard = (clientId) => api.get(`/automation/clients/${clientId}/dashboard`);
export const getAdCampaigns = (params) => api.get('/automation/ad-campaigns', { params });
export const createAdCampaign = (data) => api.post('/automation/ad-campaigns', data);
export const launchAdCampaign = (id) => api.post(`/automation/ad-campaigns/${id}/launch`);
export const deleteAdCampaign = (id) => api.delete(`/automation/ad-campaigns/${id}`);

// Campaign Tracking & Booking Integrations APIs
export const getCampaignTracking = (campaignId) => api.get(`/tracking/campaign/${campaignId}`);
export const deployCampaignTracking = (campaignId) => api.post(`/tracking/campaign/${campaignId}/deploy`);
export const setManualBookings = (deploymentId, data) => api.put(`/tracking/deployments/${deploymentId}/manual`, data);
export const updateDeploymentSettings = (deploymentId, data) => api.put(`/tracking/deployments/${deploymentId}/settings`, data);
export const getTrackingSettings = () => api.get('/tracking/settings');
export const updateTrackingSettings = (data) => api.put('/tracking/settings', data);
export const getConnections = () => api.get('/connections');
export const connectProvider = (provider) => api.post(`/connections/${provider}/connect`);
export const disconnectProvider = (provider) => api.delete(`/connections/${provider}`);
export const simulateProviderWebhook = (provider, data) => api.post(`/webhooks/${provider}/simulate`, data);

// Dashboard Tasks & Insights APIs
export const getDashboardTasks = () => api.get('/tracking/dashboard-tasks');
export const getDashboardInsights = () => api.get('/tracking/insights');
export const dismissInsight = (key) => api.post('/tracking/insights/dismiss', { key });

// Start Here APIs
export const registerTrainingInterest = () => api.post('/training-interest');

// Campaign Goal APIs
export const getGoalMap = () => api.get('/goals/map');
export const getMyGoals = () => api.get('/goals/mine');
export const getCampaignGoal = (campaignId) => api.get(`/campaigns/${campaignId}/goal`);
export const setCampaignGoal = (campaignId, goal) => api.put(`/campaigns/${campaignId}/goal`, { goal });

// Offers
export const getOfferCatalog = () => api.get('/offers/catalog');
export const getCampaignOffer = (campaignId) => api.get(`/campaigns/${campaignId}/offer`);
export const setCampaignOffer = (campaignId, offerId, customText) =>
  api.put(`/campaigns/${campaignId}/offer`, { offer_id: offerId, custom_offer_text: customText || null });

// Asset versions & customization
export const getCampaignAssetVersions = (campaignId) => api.get(`/campaigns/${campaignId}/asset-versions`);
export const getMyAssetVersion = (assetId) => api.get(`/assets/${assetId}/my-version`);
export const saveEditedVersion = (assetId, content) => api.put(`/assets/${assetId}/edited-version`, { content });
export const setActiveVersion = (assetId, version) => api.post(`/assets/${assetId}/active-version`, { version });
export const revertAssetVersion = (assetId) => api.post(`/assets/${assetId}/revert`);
export const approveAssetVersion = (assetId) => api.post(`/assets/${assetId}/approve`);
export const markAssetUsed = (assetId) => api.post(`/assets/${assetId}/mark-used`);
export const rewriteAsset = (assetId, direction, customDirection) =>
  api.post(`/assets/${assetId}/rewrite`, { direction, custom_direction: customDirection || null });
export const personalizeCampaign = (campaignId) => api.post(`/campaigns/${campaignId}/personalize`);
export const stackRewrite = (campaignId, mode) => api.post(`/campaigns/${campaignId}/stack-rewrite`, { mode });
export const getAIUsage = () => api.get('/ai/usage');
export const getAdminAIUsage = () => api.get('/admin/ai-usage');
export const uploadLogo = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/profile/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const removeLogo = () => api.delete('/profile/logo');

// AI Coach APIs
export const coachOverview = () => api.get('/coach/overview');
export const coachGrade = (campaignId) => api.get(`/coach/campaign/${campaignId}/grade`);
export const coachLandingAudit = (campaignId) => api.get(`/coach/campaign/${campaignId}/landing-audit`);
export const coachWhy = (campaignId) => api.get(`/coach/campaign/${campaignId}/why-recommended`);

// Support APIs
export const supportChat = (payload) => api.post('/support/chat', payload);
export const createSupportTicket = (payload) => api.post('/support/tickets', payload);
export const listSupportTickets = () => api.get('/support/tickets');
export const updateSupportTicket = (ticketNumber, status) => api.patch(`/support/tickets/${ticketNumber}`, { status });
export const supportUnreadCount = () => api.get('/support/tickets/unread-count');
export const preLaunchCheck = (campaignId) => api.get(`/campaigns/${campaignId}/pre-launch-check`);
export const getCanvaPayload = (assetId) => api.get(`/assets/${assetId}/canva-payload`);

// Tracking Package (Phase 3)
export const getTrackingPackage = (campaignId) => api.get(`/campaigns/${campaignId}/tracking-package`);
export const setLaunchMode = (campaignId, mode) => api.post(`/campaigns/${campaignId}/launch-mode`, { mode });
export const markCampaignLaunched = (campaignId) => api.post(`/campaigns/${campaignId}/mark-launched`);
export const setPromoCode = (campaignId, promoCode) => api.put(`/campaigns/${campaignId}/promo-code`, { promo_code: promoCode });

// Performance (Phase 4)
export const getCampaignPerformance = (campaignId) => api.get(`/campaigns/${campaignId}/performance`);
export const saveCampaignPerformance = (campaignId, data) => api.put(`/campaigns/${campaignId}/performance`, data);
export const runPerformanceCheck = (campaignId) => api.post(`/campaigns/${campaignId}/performance-check`);
export const getReadinessScore = (campaignId) => api.get(`/campaigns/${campaignId}/readiness`);

export default api;
